import { type ReactNode } from 'react'
import { ALLOWED_ATTACHMENT_MIME_TYPES, isEmpty, isNotEmpty } from '@terros/common'
import { Dropzone } from '@mantine/dropzone'
import { Box, Group, Image, Input, Stack, Text } from '@mantine/core'
import { SizePrimitives, useTranslation, useUploadCardAttachment } from '@front/model'
import { MaterialSymbolIcon, TButton } from '@components'
import { useCardGallery } from '../usePublicCard'
import classes from './CompanyGalleryUpload.module.css'
import { type CompanyCardFormProps } from './CompanyCardFields'

const MAX_CARD_IMAGE_SIZE = 8 * 1024 ** 2
const CARD_IMAGE_MIME_TYPES = ALLOWED_ATTACHMENT_MIME_TYPES.filter((contentType) => contentType.startsWith('image/'))

export const CompanyGalleryUpload = ({ form }: CompanyCardFormProps): ReactNode => {
  const { t } = useTranslation()
  const { upload, isUploading, error } = useUploadCardAttachment()
  const attachmentIds = form.values.attachmentIds ?? []
  const gallery = useCardGallery(form.values.slug, attachmentIds)

  async function uploadFiles(files: File[]): Promise<void> {
    if (isEmpty(files)) return

    const uploadedAttachmentIds = await Promise.all(files.map(upload))
    form.setFieldValue('attachmentIds', [...(form.values.attachmentIds ?? []), ...uploadedAttachmentIds])
  }

  function removePhoto(attachmentId: string): void {
    form.setFieldValue(
      'attachmentIds',
      attachmentIds.filter((id) => id !== attachmentId)
    )
  }

  return (
    <Stack gap='xs'>
      <Input.Wrapper
        description={t('{{count}} images', { count: attachmentIds.length })}
        error={error}
        label={t('Company gallery')}
        labelElement='div'
      >
        <Dropzone
          accept={CARD_IMAGE_MIME_TYPES}
          loading={isUploading}
          maxSize={MAX_CARD_IMAGE_SIZE}
          mt='xs'
          multiple
          onDrop={uploadFiles}
        >
          <Group justify='center' wrap='nowrap'>
            <Dropzone.Accept>
              <MaterialSymbolIcon color='blue' icon='add_photo_alternate' size={SizePrimitives['8']} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <MaterialSymbolIcon color='red' icon='close' size={SizePrimitives['8']} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <MaterialSymbolIcon color='dimmed' icon='add_photo_alternate' size={SizePrimitives['8']} />
            </Dropzone.Idle>
            <Stack gap={0}>
              <Text className='font-medium' size='sm'>
                {t('Add photos')}
              </Text>
              <Text c='dimmed' size='xs'>
                {t('Drag images here or click to upload. Maximum 8 MB each.')}
              </Text>
            </Stack>
          </Group>
        </Dropzone>
      </Input.Wrapper>
      {isNotEmpty(gallery) && (
        <Group gap='xs'>
          {gallery.map(({ attachmentId, url }) => (
            <Box key={attachmentId} className={classes.thumbnail}>
              <Image alt={t('Company gallery image')} fit='cover' h={80} radius='md' src={url} w={80} />
              <TButton
                buttonType='icon-full'
                className={classes.removeButton}
                icon='close'
                size='small'
                tooltip={t('Remove photo')}
                variant='destructive'
                onClick={() => removePhoto(attachmentId)}
              />
            </Box>
          ))}
        </Group>
      )}
    </Stack>
  )
}
